// MARK: - SFX Cue Assembly
// Port of SoundEffectLibrary.createSoundEffectCues (lines 99-187)
// from Mr Roast/Mr Roast/Services/Audio/SoundEffectLibrary.swift

import type { SoundEffectOpportunity, SoundEffectCue } from './types';
import type { WordTiming } from '../../types/roast';
import { findWordTiming, findWordTimingFrom, calculateTriggerTime } from './timing';
import { getEffects, selectEffect } from './sfxLibrary';
import { pick } from './rng';

// ---------------------------------------------------------------------------
// Constants (verbatim from Swift)
// ---------------------------------------------------------------------------

const RECENT_EFFECT_WINDOW = 3; // Don't repeat within last 3 effects
const MINIMUM_SPACING = 1.5;    // seconds — minimum gap between kept cues
const LAST_KEPT_INIT  = -2.0;   // allows first cue at t ≥ 0

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert sound effect opportunities into timed cues, applying:
 *   - Confidence gate (< 0.25 → skip)
 *   - Word-timing lookup (phrase not found → skip)
 *   - Effect de-duplication within a rolling window of 3
 *   - 1.5-second minimum spacing between emitted cues (greedy keep)
 *
 * Port of `SoundEffectLibrary.createSoundEffectCues(from:using:)`.
 *
 * @param opportunities - Sound effect opportunities from the AI audio profile.
 * @param wordTimings   - Word-level timings produced by Gemini TTS alignment.
 * @param rng           - Seeded PRNG; drives selectEffect and re-pick selection.
 * @param opts          - Optional behavior flags.
 *   opts.withinSegmentCursor (default false):
 *     false → global-first: each trigger phrase resolves to its first occurrence in
 *             the full wordTimings array (photo path; byte-identical pre-Task-12 behavior).
 *     true  → monotonic cursor: a repeated phrase resolves to its LATER occurrence so
 *             each deck segment gets its own word-timing slice (deck path only).
 * @returns             Sorted, spacing-filtered array of SoundEffectCue objects.
 */
export function createSoundEffectCues(
  opportunities: SoundEffectOpportunity[],
  wordTimings: WordTiming[],
  rng: () => number,
  opts?: { withinSegmentCursor?: boolean },
): SoundEffectCue[] {
  const useMonotonicCursor = opts?.withinSegmentCursor ?? false;
  const cues: SoundEffectCue[] = [];
  const recentEffects: string[] = []; // Track last 3 effects to prevent repetition
  // Monotonic cursor — only active on the deck path (withinSegmentCursor: true).
  // Advances so a REPEATED trigger phrase resolves to its later occurrence (its own
  // segment's word-timing slice). Stays at 0 on the photo path (global-first).
  let cursor = 0;

  for (const opportunity of opportunities) {
    // Include even lower confidence opportunities for more effects
    if (opportunity.confidence < 0.25) continue;

    // Resolve word timing: global-first (photo path) or within-segment cursor (deck path).
    let wordTiming: WordTiming;
    if (useMonotonicCursor) {
      const match = findWordTimingFrom(opportunity.triggerPhrase, wordTimings, cursor);
      if (match === null) continue;
      wordTiming = match.timing;
      cursor = match.index + 1; // advance past this match → a repeat scopes to a later segment
    } else {
      const found = findWordTiming(opportunity.triggerPhrase, wordTimings);
      if (found === null) continue;
      wordTiming = found;
    }

    // Select effect — Swift's selectEffect(for:) had no lastUsed param; the TS
    // port added lastUsed to thread into selectSmartDefault.  Pass the most-
    // recently tracked effect (last element of recentEffects) so smart defaults
    // avoid immediate repetition even before the recentEffects check below.
    const lastUsed = recentEffects.length > 0
      ? recentEffects[recentEffects.length - 1]
      : undefined;
    let effectName: string | null = selectEffect(opportunity, lastUsed, rng);

    // If selected effect was recently used, try to find alternative
    if (effectName !== null && recentEffects.includes(effectName)) {
      // Get all effects for this category
      const categoryEffects = getEffects(opportunity.suggestedCategory);

      // Filter out recent effects
      const availableEffects = categoryEffects.filter(e => !recentEffects.includes(e));

      if (availableEffects.length > 0) {
        // Pick a random alternative from available effects (seeded rng mirrors Swift's randomElement())
        effectName = pick(rng, availableEffects);
      } else {
        // If no alternatives, skip this opportunity to avoid repetition
        continue;
      }
    }

    if (effectName === null) continue;

    // Calculate trigger time based on timing preference
    const triggerTime = calculateTriggerTime(opportunity.timing, wordTiming);

    // Create cue
    const cue: SoundEffectCue = {
      effectName,
      triggerTime,
      volume: 0.9,
      category: opportunity.suggestedCategory,
    };

    cues.push(cue);

    // Update recent effects tracking
    recentEffects.push(effectName);
    if (recentEffects.length > RECENT_EFFECT_WINDOW) {
      recentEffects.shift(); // removeFirst() in Swift
    }
  }

  // Sort by trigger time
  cues.sort((a, b) => a.triggerTime - b.triggerTime);

  // Apply timing constraints to prevent overlapping effects
  const finalCues: SoundEffectCue[] = [];
  let lastTriggerTime = LAST_KEPT_INIT; // Start with -2 to allow first effect

  for (const cue of cues) {
    // Ensure minimum spacing between effects (1.5 seconds for smooth flow)
    if (cue.triggerTime >= lastTriggerTime + MINIMUM_SPACING) {
      finalCues.push(cue);
      lastTriggerTime = cue.triggerTime;
    }
  }

  return finalCues;
}
